import React, { useEffect } from "react";
import styled from "styled-components";

import Analytics from "js/analytics";
import { Button } from "js/Components/Button";
import { getStaticUrl } from "js/config";
import { notificationEvents as notificationEventsApi } from "apis-v2/callables";
import { ActionPermissionsObject, PermissionActionType } from "common/interfaces";
import AppController from "js/core/AppController";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { t } from "js/i18n";
import { BeautifulDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { Gap10 } from "js/react/components/Gap";
import { InviteSection } from "js/react/views/Billing/dialogs/components/InviteSection";
import getLogger, { LogGroup } from "js/core/logger";
import { useInviteMembersWithBilling } from "js/react/views/Billing/dialogs/hooks/useInviteMembersWithBilling";
import { settingsLabel } from "js/textStyles";
import ux from "js/core/services/userExperiences";
import { app } from "js/namespaces";

const logger = getLogger(LogGroup.BILLING);

const Layout = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 40px 50px;
    gap: 50px;
`;

const FormPane = styled.div`
    flex: 1;
    min-width: 0;
    gap: 30px;
    display: flex;
    flex-direction: column;
`;

const IllustrationPane = styled.div`
    display: flex;
    flex-direction: column;
    width: 302px;
`;

const Illustration = styled.img`
    display: block;
    width: 100%;
    height: auto;
`;

const Benefits = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0 20px;
    flex: 1;
    justify-content: center;
`;

const BenefitItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const CheckMark = styled.img`
    width: 9px;
    height: 8px;
    flex-shrink: 0;
`;

const BenefitText = styled.span`
    color: #333;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-weight: 600;
    line-height: 140%;
`;

const Title = styled.div`
    color: #333;
    font-family: "Source Sans Pro";
    font-size: 22px;
    font-style: normal;
    font-weight: 600;
    line-height: 140%;
`;

const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
`;

const Logo = styled.img`
    width: 50px;
    height: 50px;
`;

const Description = styled.p`
    color: #000;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%;
    height: 59px;
`;

const SecondaryDescription = styled.p`
    color: #666;
    line-height: 120%;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
`;

const MembersSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

const Actions = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const SectionTitle = styled.div`
    ${settingsLabel}
`;

const BENEFITS = [
    "Collaborate on decks in real time",
    "Share and manage brand assets",
    "Build better presentations, together"
];

interface AddTeamDialogProps {
    closeDialog: () => void;
    notification?: { id: string; event?: { workspaceId?: string; teamId?: string } };
}

function AddTeamDialog(
    props: AddTeamDialogProps & WorkspaceControllerState & BillingControllerState,
    ref: React.Ref<any>
) {
    const { closeDialog, workspace, users, userInvites, notification } = props;

    const remainingIncludedSeats = BillingController.remainingTieredPlanIncludedSeatCount;
    const notificationWorkspaceId = notification?.event?.workspaceId ?? notification?.event?.teamId;
    const canManageBilling = WorkspaceController.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE];
    const shouldShow = !app.isConstrained &&
        !app.isMobileOrTablet &&
        !(notificationWorkspaceId && notificationWorkspaceId !== workspace.id) &&
        canManageBilling &&
        remainingIncludedSeats > 0;

    const existingUserEmails = [
        ...users.map(u => u?.email?.toLowerCase?.()).filter(Boolean),
        ...userInvites.map(u => u?.email?.toLowerCase?.()).filter(Boolean)
    ] as string[];

    const {
        members,
        handleEmailsChange,
        hasError,
        setHasError,
        membersAreValid,
        additionalSeatCount,
        executeInvite,
        isInvitingSelf,
        filteredMembers
    } = useInviteMembersWithBilling({
        availableSeatCount: remainingIncludedSeats,
        existingUserEmails
    });

    const handleClose = () => {
        closeDialog();
    };

    useEffect(() => {
        if (!shouldShow) {
            // Skip without consuming: wrong workspace / mobile / no seats yet must not burn the one-shot.
            closeDialog();
            return;
        }

        // Seen = dialog actually shown (not dismiss). Starts the next stagger timer.
        if (notification?.id) {
            AppController.notificationsService.markAsRead(notification.id)
                .catch(err => logger.error(err, "[AddTeamDialog] Failed to mark unused-seats notification as read", { id: notification.id }));
        }

        const seenReminderIndex = notification?.event?.reminderIndex;
        const workspaceIdForReminder = notificationWorkspaceId ?? workspace.id;
        if (Number.isInteger(seenReminderIndex) && BillingController.remainingTieredPlanIncludedSeatCount > 0) {
            notificationEventsApi.scheduleNextUnusedSeatsDialogReminder({
                workspaceId: workspaceIdForReminder,
                seenReminderIndex
            }).catch(err => logger.error(err, "[AddTeamDialog] Failed to schedule next unused-seats dialog reminder", {
                workspaceId: workspaceIdForReminder,
                seenReminderIndex
            }));
        }

        Analytics.ModifyWorkspaceUsers.InviteUsersStart({ workspace_id: workspace.id });

        ux.inviteUsersToWorkspace.register({
            workspaceId: workspace.id,
            tier: WorkspaceController.plan?.tier
        });

        return () => {
            ux.inviteUsersToWorkspace.abandon();
        };
    }, []);

    if (!shouldShow) {
        return null;
    }

    const canInvite = additionalSeatCount <= 0 && membersAreValid && !isInvitingSelf;

    const handleInvite = async () => {
        // it should never be possible to invite more than maxEmailCount
        // but just adding it here for safety
        if (additionalSeatCount > 0) {
            return;
        }

        ux.inviteUsersToWorkspace.update({ additionalSeatCount, invitedUserCount: filteredMembers.length });

        try {
            await executeInvite();
            ux.inviteUsersToWorkspace.complete();
            handleClose();
        } catch (err) {
            logger.error(err, "[AddTeamDialog] Failed to invite members");
            ux.inviteUsersToWorkspace.fail({ error: err }, undefined, true);

            ShowErrorDialog({
                title: t("Failed to invite members"),
                message: err.message
            });
        }
    };

    return (
        <BeautifulDialog
            closeDialog={handleClose}
            ref={ref}
            maxWidth={false}
            PaperProps={{ style: { width: 800 } }}
            data-test-id="add-team-dialog"
        >
            <Layout>
                <FormPane>
                    <TitleBlock>
                        <Logo src={getStaticUrl("/images/beautifulai-logos/beautifulai-logo-round.svg")} alt="" />
                        <Gap10 />
                        <Title>
                            {t("Add your team to get the most out of Beautiful.ai")}
                        </Title>
                        <Description>
                            {t("Don't forget to use the seats you have available! Invite your team members now to start creating together.")}
                        </Description>
                        <SecondaryDescription>
                            {t("Add members any time from your account settings.")}
                        </SecondaryDescription>
                    </TitleBlock>
                    <MembersSection>
                        <SectionTitle>{t("Add Members")}</SectionTitle>
                        <InviteSection
                            emails={members.map(m => m.email)}
                            onEmailsChange={handleEmailsChange}
                            existingUserEmails={existingUserEmails}
                            hasError={hasError}
                            setHasError={setHasError}
                            maxEmailCount={remainingIncludedSeats}
                        />
                    </MembersSection>
                    <Actions>
                        <Button onClick={handleClose} unframed large>
                            {t("Later")}
                        </Button>
                        <Button blue large onClick={handleInvite} disabled={!canInvite} data-test-id="add-team-dialog-invite-button">
                            {t("Add members")}
                        </Button>
                    </Actions>
                </FormPane>
                <IllustrationPane>
                    <Illustration
                        src={getStaticUrl("/images/invite_user_dialog.svg")}
                        alt=""
                    />
                    <Benefits>
                        {BENEFITS.map(benefit => (
                            <BenefitItem key={benefit}>
                                <CheckMark
                                    src={getStaticUrl("/images/dialogs/check-mark.svg")}
                                    alt=""
                                />
                                <BenefitText>{t(benefit)}</BenefitText>
                            </BenefitItem>
                        ))}
                    </Benefits>
                </IllustrationPane>
            </Layout>
        </BeautifulDialog>
    );
}

export default WorkspaceController.withInitializedState(
    BillingController.withState(React.forwardRef(AddTeamDialog))
);
